import React, { useEffect, useState } from 'react';
import {
  Box,
  CircularProgress,
  Alert,
  Grid,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
} from '@mui/material';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ListPageLayout from '../components/shared/list-page-layout';
import ListFilters, { FilterField } from '../components/shared/list-filters';
import { IListQuestionAnwserRequest } from '../../model/base/request/list-question-anwser-request';
import { useFAQController } from './context/faq-controller';
import { useFAQState } from './context/faq-context';

export function FAQComponent() {
  const { loadFAQs, loadCategories } = useFAQController();
  const { faqs, categories, loading } = useFAQState();
  const [filters, setFilters] = useState<IListQuestionAnwserRequest>({
    page: 0,
    pageSize: 100,
    term: '',
    categoryId: undefined,
  });

  useEffect(() => {
    loadCategories();
    loadFAQs(filters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const categoryOptions = categories
    .filter(cat => cat.id !== undefined && cat.description !== undefined)
    .map(cat => ({
      value: cat.id!,
      label: cat.description!
    }));

  const filterFields: FilterField[] = [
    {
      name: 'term',
      label: 'Pesquisar',
      type: 'text',
      placeholder: 'Buscar por pergunta ou resposta',
      fullWidth: false,
      gridSize: { xs: 12, sm: 12, md: 8 },
    },
    {
      name: 'categoryId',
      label: 'Categoria',
      type: 'select',
      options: categoryOptions,
      gridSize: { xs: 12, sm: 12, md: 4 },
    },
  ];

  const handleFilterChange = (name: string, value: any) => {
    setFilters((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSearch = () => {
    loadFAQs(filters);
  };

  const handleClearFilters = () => {
    const clearedFilters: IListQuestionAnwserRequest = {
      page: 0,
      pageSize: 100,
      term: '',
      categoryId: undefined,
    };
    setFilters(clearedFilters);
    loadFAQs(clearedFilters);
  };

  return (
    <ListPageLayout
      title="Perguntas Frequentes"
      subtitle="Encontre respostas para as principais dúvidas"
      breadcrumbs={[
        { label: 'Perguntas Frequentes' }
      ]}
      filters={
        <ListFilters
          fields={filterFields.map((field) => ({
            ...field,
            value: filters[field.name as keyof IListQuestionAnwserRequest],
          }))}
          onFilterChange={handleFilterChange}
          onSearch={handleSearch}
          onClear={handleClearFilters}
          isLoading={loading}
        />
      }
    >
      {loading ? (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: 400,
          }}
        >
          <CircularProgress size={60} />
        </Box>
      ) : faqs.length === 0 ? (
        <Alert severity="info">
          {filters.term || filters.categoryId
            ? 'Nenhuma pergunta frequente encontrada com os filtros aplicados.'
            : 'Nenhuma pergunta frequente cadastrada no momento.'}
        </Alert>
      ) : (
        <Grid container spacing={2}>
          {faqs.map((faq, index) => (
            <Grid item xs={12} key={faq.id || index}>
              <Accordion
                elevation={2}
                sx={{
                  '&:before': {
                    display: 'none',
                  },
                  borderRadius: 1,
                  overflow: 'hidden',
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  sx={{
                    backgroundColor: 'action.hover',
                    transition: 'background-color 0.2s ease',
                    '&:hover': {
                      backgroundColor: 'primary.main',
                      '& .help-icon': {
                        color: 'white',
                      },
                      '& .question-text': {
                        color: 'white',
                      },
                      '& .category-text': {
                        color: 'rgba(255, 255, 255, 0.8)',
                      },
                      '& .MuiSvgIcon-root': {
                        color: 'white',
                      },
                    },
                    '& .MuiAccordionSummary-content': {
                      alignItems: 'center',
                      gap: 2,
                    },
                    '& .MuiAccordionSummary-expandIconWrapper': {
                      transition: 'color 0.2s ease',
                    },
                  }}
                >
                  <HelpOutlineIcon
                    className="help-icon"
                    sx={{
                      color: 'primary.main',
                      transition: 'color 0.2s ease',
                    }}
                  />
                  <Box sx={{ flex: 1 }}>
                    <Typography
                      variant="h6"
                      className="question-text"
                      sx={{
                        fontWeight: 600,
                        color: 'text.primary',
                        transition: 'color 0.2s ease',
                      }}
                    >
                      {faq.question}
                    </Typography>
                    {faq.category && (
                      <Typography
                        variant="caption"
                        className="category-text"
                        sx={{
                          color: 'text.secondary',
                          mt: 0.5,
                          display: 'block',
                          transition: 'color 0.2s ease',
                        }}
                      >
                        Categoria: {faq.category}
                      </Typography>
                    )}
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography
                    variant="body1"
                    color="text.secondary"
                    sx={{
                      whiteSpace: 'pre-wrap',
                      lineHeight: 1.8,
                    }}
                  >
                    {faq.answer}
                  </Typography>
                </AccordionDetails>
              </Accordion>
            </Grid>
          ))}
        </Grid>
      )}
    </ListPageLayout>
  );
}
